import { Component, Input } from '@angular/core';
import { AiService } from '../../services/ai.service';
import { ToastrService } from 'ngx-toastr';

type SimilarKeyword = {
  topic: string;
  reason?: string;
  selected: boolean;
};

type SimilarContentItem = {
  title: string;
  url: string;
  source?: string;
  reason?: string;
};

type SimilarPlatform = {
  id: string;
  label: string;
  source: string;
};

const PLATFORM_OPTIONS: SimilarPlatform[] = [
  { id: 'youtube', label: 'YouTube', source: 'YouTube' },
  { id: 'wikipedia', label: 'Wikipedia', source: 'Wikipedia' },
  { id: 'web', label: 'Web', source: 'Web' },
  { id: 'scholar', label: 'Scholar', source: 'Scholar' },
];

@Component({
  selector: 'tangram-similar-content',
  templateUrl: './similar-content.component.html',
  styleUrls: ['./similar-content.component.scss'],
})
export class SimilarContentComponent {
  @Input() pathwayId: string = '';
  @Input() videoId: string = '';
  @Input() title: string = '';
  @Input() description: string = '';
  /** @deprecated Prefer title + description; kept for legacy callers */
  @Input() seed: string = '';
  @Input() mode: string = '';

  keywords: SimilarKeyword[] = [];
  platforms: SimilarPlatform[] = PLATFORM_OPTIONS;
  selectedPlatformIds: string[] = PLATFORM_OPTIONS.map((p) => p.id);
  loading = false;
  refining = false;
  loaded = false;
  loadingSuggestions = false;
  showSuggestions = false;
  similarContentItems: SimilarContentItem[] = [];

  constructor(
    private aiService: AiService,
    private toastr: ToastrService,
  ) {}

  get selectedKeywords(): SimilarKeyword[] {
    return this.keywords.filter((k) => k.selected && !!(k.topic || '').trim());
  }

  get selectedCount(): number {
    return this.selectedKeywords.length;
  }

  get allKeywordsSelected(): boolean {
    const validKeywords = this.keywords.filter((keyword) => !!(keyword.topic || '').trim());
    return !!validKeywords.length && validKeywords.every((keyword) => keyword.selected);
  }

  get allPlatformsSelected(): boolean {
    return this.selectedPlatformIds.length === this.platforms.length;
  }

  get selectedPlatformCount(): number {
    return this.selectedPlatformIds.length;
  }

  get hasContentContext(): boolean {
    return !!(this.resolvedTitle || this.resolvedDescription || (this.seed || '').trim());
  }

  get resolvedTitle(): string {
    return (this.title || '').trim();
  }

  get resolvedDescription(): string {
    return (this.description || '').trim();
  }

  get contentSeed(): string {
    const title = this.resolvedTitle;
    const description = this.resolvedDescription;
    if (title && description) return `${title}. ${description}`;
    return title || description || (this.seed || '').trim();
  }

  load(refine = false): void {
    if (!this.hasContentContext) {
      this.toastr.warning('No content available to find related topics for.');
      return;
    }

    const selectedTopics = this.selectedKeywords.map((k) => k.topic.trim());
    if (refine && !selectedTopics.length) {
      this.toastr.warning('Select at least one keyword to refine suggestions.');
      return;
    }

    if (refine) {
      this.refining = true;
    } else {
      this.loading = true;
    }

    this.aiService.getSimilarContent(this.contentSeed, {
      title: this.resolvedTitle || undefined,
      description: this.resolvedDescription || undefined,
      pathwayId: this.pathwayId || undefined,
      videoId: this.videoId || undefined,
      mode: this.mode || undefined,
      refine,
      selectedKeywords: refine ? selectedTopics : undefined,
    }).subscribe(
      (res: any) => {
        const data = (res && res.data) || {};
        const fromApi = Array.isArray(data.keywords) ? data.keywords : [];
        const incoming = this.normalizeKeywords(fromApi, data.items);

        if (refine) {
          this.keywords = this.mergeKeywords(this.keywords, incoming);
        } else {
          this.keywords = incoming;
          this.showSuggestions = false;
          this.similarContentItems = [];
        }

        this.loaded = true;
        this.loading = false;
        this.refining = false;
      },
      () => {
        if (!refine) {
          this.keywords = this.buildClientFallbackKeywords();
          this.loaded = true;
        }
        this.loading = false;
        this.refining = false;
      },
    );
  }

  toggleKeyword(keyword: SimilarKeyword): void {
    keyword.selected = !keyword.selected;
    this.showSuggestions = false;
  }

  toggleAllKeywords(checked: boolean): void {
    this.keywords.forEach((keyword) => {
      if ((keyword.topic || '').trim()) {
        keyword.selected = checked;
      }
    });
    this.showSuggestions = false;
  }

  addKeyword(): void {
    this.keywords.push({
      topic: '',
      reason: 'Custom keyword',
      selected: true,
    });
    this.showSuggestions = false;
  }

  removeKeyword(index: number): void {
    this.keywords.splice(index, 1);
    this.showSuggestions = false;
  }

  onKeywordInputChanged(): void {
    this.showSuggestions = false;
  }

  toggleAllPlatforms(checked: boolean): void {
    this.selectedPlatformIds = checked ? this.platforms.map((p) => p.id) : [];
    this.showSuggestions = false;
  }

  getSimilarContentSuggestions(): void {
    const selectedTopics = this.selectedKeywords.map((k) => k.topic.trim()).filter(Boolean);
    if (!selectedTopics.length) {
      this.toastr.warning('Select at least one keyword to get similar content.');
      return;
    }
    if (!this.selectedPlatformIds.length) {
      this.toastr.warning('Select at least one platform.');
      return;
    }

    const keywordSeed = selectedTopics.join('. ');
    this.loadingSuggestions = true;

    const selectedPlatformSources = this.getSelectedPlatformSources();

    this.aiService.getSimilarContent(keywordSeed, {
      title: keywordSeed,
      description: this.resolvedDescription || undefined,
      pathwayId: this.pathwayId || undefined,
      videoId: this.videoId || undefined,
      mode: this.mode || undefined,
      selectedKeywords: selectedTopics,
      selectedPlatforms: selectedPlatformSources,
    }).subscribe(
      (res: any) => {
        const data = (res && res.data) || {};
        this.similarContentItems = this.normalizeItems(data.items, selectedPlatformSources);
        this.showSuggestions = true;
        this.loadingSuggestions = false;
      },
      () => {
        this.similarContentItems = [];
        this.showSuggestions = true;
        this.loadingSuggestions = false;
      },
    );
  }

  private mergeKeywords(existing: SimilarKeyword[], incoming: SimilarKeyword[]): SimilarKeyword[] {
    const merged = [...existing];
    const seen = new Set(existing.map((entry) => this.normalizeTopicKey(entry.topic)));

    incoming.forEach((entry) => {
      const key = this.normalizeTopicKey(entry.topic);
      if (!key || seen.has(key)) return;
      seen.add(key);
      merged.push({ ...entry, selected: false });
    });

    return merged.slice(0, 12);
  }

  private normalizeKeywords(
    fromApi: any[],
    items: any[] | undefined,
  ): SimilarKeyword[] {
    const fromKeywords = this.dedupeKeywordEntries(
      (fromApi || [])
        .map((entry) => ({
          topic: String((entry && entry.topic) || '').trim(),
          reason: String((entry && entry.reason) || '').trim(),
        }))
        .filter((entry) => !!entry.topic),
    );

    if (fromKeywords.length) {
      return fromKeywords.map((entry) => this.toKeywordModel(entry, true));
    }

    const fromItems = this.dedupeKeywordEntries(
      (items || [])
        .map((item) => ({
          topic: this.extractTopicFromItemTitle(String((item && item.title) || '')),
          reason: String((item && item.reason) || '').trim(),
        }))
        .filter((entry) => !!entry.topic),
    );

    if (fromItems.length) {
      return fromItems.map((entry) => this.toKeywordModel(entry, true));
    }

    return this.buildClientFallbackKeywords();
  }

  private toKeywordModel(entry: { topic: string; reason?: string }, selected: boolean): SimilarKeyword {
    return {
      topic: entry.topic,
      reason: entry.reason || '',
      selected,
    };
  }

  private dedupeKeywordEntries(entries: Array<{ topic: string; reason?: string }>): Array<{ topic: string; reason?: string }> {
    const seen = new Set<string>();
    const unique: Array<{ topic: string; reason?: string }> = [];

    entries.forEach((entry) => {
      const key = this.normalizeTopicKey(entry.topic);
      if (!key || seen.has(key)) return;
      seen.add(key);
      unique.push(entry);
    });

    return unique.slice(0, 8);
  }

  private normalizeTopicKey(topic: string): string {
    return String(topic || '')
      .toLowerCase()
      .replace(/[^\w\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private extractTopicFromItemTitle(title: string): string {
    const trimmed = (title || '').trim();
    if (!trimmed) return '';
    const match = trimmed.match(/^Search .+ for "(.+)"$/i);
    return match ? match[1].trim() : trimmed;
  }

  private buildKeywordCandidatesFromText(text: string, reason: string): Array<{ topic: string; reason: string }> {
    const cleaned = String(text || '').trim();
    if (!cleaned || /^https?:\/\//i.test(cleaned)) return [];

    const words = cleaned.replace(/[^\w\s-]/g, ' ').split(/\s+/).filter(Boolean);
    if (!words.length) return [];

    const candidates: Array<{ topic: string; reason: string }> = [];
    if (words.length >= 4) {
      candidates.push({ topic: words.slice(0, 4).join(' '), reason });
    }
    if (words.length >= 2) {
      candidates.push({ topic: words.slice(0, 2).join(' '), reason });
    }
    if (words.length >= 6) {
      candidates.push({ topic: words.slice(2, 6).join(' '), reason });
    }
    if (!candidates.length) {
      candidates.push({ topic: cleaned.slice(0, 80), reason });
    }

    return candidates;
  }

  private buildClientFallbackKeywords(): SimilarKeyword[] {
    const titleCandidates = this.buildKeywordCandidatesFromText(this.resolvedTitle, 'From title');
    const descriptionCandidates = this.buildKeywordCandidatesFromText(this.resolvedDescription, 'From description');
    const seedCandidates = (!this.resolvedTitle && !this.resolvedDescription)
      ? this.buildKeywordCandidatesFromText(this.contentSeed, 'Based on this content')
      : [];

    return this.dedupeKeywordEntries([
      ...titleCandidates,
      ...descriptionCandidates,
      ...seedCandidates,
    ]).map((entry) => this.toKeywordModel(entry, true));
  }

  private getSelectedPlatformSources(): string[] {
    return this.platforms
      .filter((platform) => this.selectedPlatformIds.includes(platform.id))
      .map((platform) => platform.source);
  }

  private normalizeItems(items: any[] | undefined, allowedSources: string[] = []): SimilarContentItem[] {
    const seen = new Set<string>();
    const normalized: SimilarContentItem[] = [];
    const allowedSet = new Set((allowedSources || []).map((value) => value.toLowerCase()));

    (items || []).forEach((item) => {
      const title = String((item && item.title) || '').trim();
      const url = String((item && item.url) || '').trim();
      const source = String((item && item.source) || '').trim();
      if (!title || !url) return;
      if (allowedSet.size && (!source || !allowedSet.has(source.toLowerCase()))) return;

      const key = `${title.toLowerCase()}|${url.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);

      normalized.push({
        title,
        url,
        source,
        reason: String((item && item.reason) || '').trim(),
      });
    });

    return normalized.slice(0, 32);
  }
}
